// ENG-054: which conversation thread the widget shows, and when it switches.
//
// The thread follows the product the host shows (utils/url.ts getCurrentProductSku: the host's
// hostContext.productSku first, then the page URL). The host product is read on mount, on
// "public-widget:host-context" (ChatWidget.setHostContext, e.g. Genie) and on "popstate" (SPA
// navigation on the site). Chat.tsx also re-reads it right before sending a new message, so a
// message always goes to the thread of the product the host shows at that moment (or, ENG-055, of
// the pinned product while the host still shows what it showed at the pin). While an answer
// is loading or streaming a switch waits until it ends; nothing is cancelled. "General chat" (the
// chip) applies to the current product only and is forgotten when the product changes. Widget
// mode only: the standalone app always has the general thread, as before.
//
// ENG-055: a product picked from "Recent" (chooseRecent) is "pinned": it is the thread's product
// instead of the host's, also when the host shows another product or none. The pin is dropped as
// soon as the host's product differs from what it was at the pin (another product, or none, or
// one where there was none); the same product again, e.g. the re-read before a send, keeps it.
// "General chat" applies to the thread's product, pinned or not.
//
// ENG-056: the actions are the same; they are rows of the "Recent" menu now (ProductThreadBar).
//
// Event handlers can run in the same tick as a host call, before React re-renders, so the whole
// selection (host product, general-chat choice, pin) is kept in a ref too; current() reads that.
// The transitions themselves are pure functions in utils/conversationThread.ts.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
    ConversationThreadState,
    initialThreadSelection,
    resolveThreadSelection,
    selectionAfterChooseGeneral,
    selectionAfterChooseHost,
    selectionAfterChooseProduct,
    selectionAfterChooseRecent,
    selectionAfterHostRead,
    ThreadSelection,
    ThreadSwitchNotice
} from "../../utils/conversationThread";
import { getCurrentProductSku } from "../../utils/url";

interface UseConversationThreadArgs {
    isWidget: boolean;
    // An answer is loading or streaming: switches wait until it ends.
    busy: boolean;
    // The thread on screen has messages (for "your previous chat is saved").
    hasMessages: boolean;
}

export function useConversationThread({ isWidget, busy, hasMessages }: UseConversationThreadArgs) {
    const [initialSelection] = useState<ThreadSelection>(() => initialThreadSelection(isWidget ? getCurrentProductSku() : null));
    const [selection, setSelection] = useState<ThreadSelection>(initialSelection);
    const [notice, setNotice] = useState<ThreadSwitchNotice | null>(null);

    const selectionRef = useRef<ThreadSelection>(initialSelection);
    const pendingHostSyncRef = useRef(false);
    const busyRef = useRef(busy);
    busyRef.current = busy;
    const hasMessagesRef = useRef(hasMessages);
    hasMessagesRef.current = hasMessages;

    // The thread as of the latest event or click, even before React re-rendered.
    const current = useCallback((): ConversationThreadState => resolveThreadSelection(isWidget, selectionRef.current), [isWidget]);

    const switchTo = useCallback(
        (next: ThreadSelection) => {
            if (next === selectionRef.current) return;
            const before = current().threadKey;
            selectionRef.current = next;
            setSelection(next);
            const after = current();
            if (after.threadKey !== before) {
                setNotice({ to: after.thread, previousSaved: hasMessagesRef.current });
            }
        },
        [current]
    );

    // Re-read the host's product. A new product forgets the general-chat choice and the pin.
    const syncWithHost = useCallback(() => {
        if (!isWidget) return;
        if (busyRef.current) {
            pendingHostSyncRef.current = true;
            return;
        }
        pendingHostSyncRef.current = false;
        switchTo(selectionAfterHostRead(selectionRef.current, getCurrentProductSku()));
    }, [isWidget, switchTo]);

    useEffect(() => {
        if (!isWidget) return;
        window.addEventListener("public-widget:host-context", syncWithHost);
        window.addEventListener("popstate", syncWithHost);
        // pushState/replaceState (SPA forward navigation) — widget.tsx dispatches this from its history hooks.
        window.addEventListener("public-widget:location", syncWithHost);
        // Catch a host change between the first render and these listeners.
        syncWithHost();
        return () => {
            window.removeEventListener("public-widget:host-context", syncWithHost);
            window.removeEventListener("popstate", syncWithHost);
            window.removeEventListener("public-widget:location", syncWithHost);
        };
    }, [isWidget, syncWithHost]);

    // A message started: the switch line has done its job. An answer ended: apply a switch that
    // arrived while it was running.
    useEffect(() => {
        if (busy) setNotice(null);
        else if (pendingHostSyncRef.current) syncWithHost();
    }, [busy, syncWithHost]);

    // "General chat" while in the product's thread...
    const chooseGeneral = useCallback(() => {
        if (busyRef.current) return;
        switchTo(selectionAfterChooseGeneral(selectionRef.current));
    }, [switchTo]);

    // ...and the thread's product, back from the general thread.
    const chooseProduct = useCallback(() => {
        if (busyRef.current) return;
        switchTo(selectionAfterChooseProduct(selectionRef.current));
    }, [switchTo]);

    // ENG-055: the host's product while another one is pinned: the host's product thread.
    const chooseHost = useCallback(() => {
        if (busyRef.current) return;
        switchTo(selectionAfterChooseHost(selectionRef.current));
    }, [switchTo]);

    // ENG-055: "Recent": pin that product's thread. The host's product is read now, so the pin is
    // measured against what the host shows at this moment.
    const chooseRecent = useCallback(
        (sku: string) => {
            if (!isWidget || busyRef.current || !sku) return;
            pendingHostSyncRef.current = false;
            switchTo(selectionAfterChooseRecent(sku, getCurrentProductSku()));
        },
        [isWidget, switchTo]
    );

    const clearNotice = useCallback(() => setNotice(null), []);

    const state = useMemo(() => resolveThreadSelection(isWidget, selection), [isWidget, selection]);

    return {
        ...state,
        hostSku: selection.hostSku,
        // ENG-055: the product picked from "Recent", while it is the thread's product.
        pinnedSku: selection.pinnedSku,
        notice,
        current,
        syncWithHost,
        chooseGeneral,
        chooseProduct,
        chooseHost,
        chooseRecent,
        clearNotice
    };
}
